import { useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  Flex,
  Card,
  Text,
  Button,
  Heading,
  Separator,
  Input,
  toast,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import {
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import styles from './BillingEmail.module.css'

export const BillingEmail_BillingDetails = graphql(/* GraphQL */ `
  fragment BillingEmail_BillingDetails on BillingDetails {
    billingEmail
  }
`)

export const UpdateBillingEmailMutation = graphql(/* GraphQL */ `
  mutation UpdateBillingEmail($input: UpdateOrganizationBillingEmailInput!) {
    updateOrganizationBillingEmail(input: $input) {
      ... on UpdateOrganizationBillingEmailSuccess {
        billingEmail
        success
      }
      ... on BillingEmailValidationError {
        message
        invalidEmail
      }
    }
  }
`)

const isValidEmail = (email: string): boolean => {
  if (!email) return false // Empty is NOT valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const BillingEmail = ({
  billingDetails,
}: {
  billingDetails: FragmentOf<typeof BillingEmail_BillingDetails>
}): JSX.Element | null => {
  const organizationId = useOrganizationId()
  const canManageBilling = useHasOrganizationAction('manageBilling')

  const currentBillingEmail = readFragment(
    BillingEmail_BillingDetails,
    billingDetails,
  ).billingEmail

  const [email, setEmail] = useState(currentBillingEmail ?? '')
  const [emailError, setEmailError] = useState<string | null>(null)

  const [updateBillingEmail, { loading: updating }] = useMutation(
    UpdateBillingEmailMutation,
    {
      onCompleted: (mutationData) => {
        const result = mutationData?.updateOrganizationBillingEmail
        if (!result) return

        if (result.__typename === 'UpdateOrganizationBillingEmailSuccess') {
          toast.success('Billing email updated successfully')

          BufferTracker.billingEmailUpdated({
            organizationId: organizationId ?? '',
            clientName: Client.PublishWeb,
            previousBillingEmail: currentBillingEmail ?? '',
            newBillingEmail: email,
          })
        } else if (result.__typename === 'BillingEmailValidationError') {
          setEmailError(result.message)
          toast.error(result.message)
        }
      },
      onError: () => {
        toast.error(
          'Failed to update billing email. Try again or contact support if this issue persists.',
        )
      },
      refetchQueries: ['BillingPage'],
    },
  )

  if (!canManageBilling) {
    return null
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value
    setEmail(newEmail)

    // Clear error when user starts typing
    if (emailError) {
      setEmailError(null)
    }

    // Validate email format
    if (!isValidEmail(newEmail)) {
      if (!newEmail) {
        setEmailError('Email is required')
      } else {
        setEmailError('Please enter a valid email address')
      }
    }
  }

  const handleSave = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault()

    // Validate before saving
    if (!isValidEmail(email)) {
      if (!email) {
        setEmailError('Email is required')
      } else {
        setEmailError('Please enter a valid email address')
      }
      return
    }

    if (!organizationId) return

    await updateBillingEmail({
      variables: {
        input: {
          organizationId,
          billingEmail: email,
        },
      },
    })
  }

  const hasChanges = email !== currentBillingEmail
  const isSaveDisabled = updating || !hasChanges || !isValidEmail(email)

  return (
    <>
      <Separator className={styles.fullWidth} />
      <Heading as="h2" size="small">
        Billing Email
      </Heading>
      <Card className={styles.card}>
        <Flex direction="column" gap="md">
          <Text size="md" color="subtle">
            Update the email address below to change where invoices and billing
            notifications are sent. By default, invoices are sent to the
            organization owner&apos;s email.
          </Text>

          <Flex direction="column" gap="xs" className={styles.fullWidth}>
            <Flex
              direction="row"
              gap="xs"
              align="end"
              className={styles.fullWidth}
            >
              <Input
                id="billing-email"
                name="billingEmail"
                type="email"
                autoComplete="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="billing@company.com"
                disabled={updating}
                aria-label="Billing email address"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'email-error' : undefined}
                className={styles.emailInput}
              />
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                className={styles.fixedWidthButton}
              >
                {updating ? 'Saving...' : 'Save'}
              </Button>
            </Flex>
            {emailError && (
              <Text size="sm" color="critical" id="email-error">
                {emailError}
              </Text>
            )}
          </Flex>
        </Flex>
      </Card>
    </>
  )
}
